import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import CONFIG from '../config/config';

/**
 * Fetch a user by their email.
 * @param {string} email - The email address of the user.
 * @returns {Promise<Object>} - The user data if found.
 */
export const getAccountByEmail = async (email) => {
    try {
        const url = `${CONFIG.API.BASE_URL}/cloud-accounts/get-by-email`;
        const response = await axios.get(url, {
            params: { email },
            headers: {
                ...CONFIG.DEFAULT_HEADERS,
                accessToken: CONFIG.API.ACCESS_TOKEN,
            },
        });

        if (response.status === 200) {
            return response.data.cloudAccount;
        } else {
            throw new Error(`Unexpected status code: ${response.status}`);
        }
    } catch (error) {
        if (error.response && error.response.status === 404) {
            console.warn(`Account not found for email: ${email}`);
            return null; // Handle 404 case by returning null
        } else {
            console.error('Error fetching account by email:', error);
            throw error;
        }
    }
};

/**
 * Fetch an account by its accountGuid.
 * @param {string} accountGuid - The unique identifier of the account.
 * @returns {Promise<Object>} - The account data if found.
 */
export const getAccountByGuid = async (accountGuid) => {
    try {
        const url = `${CONFIG.API.BASE_URL}/cloud-accounts/get-by-guid`;
        const response = await axios.get(url, {
            params: { accountGuid },
            headers: {
                ...CONFIG.DEFAULT_HEADERS,
                accessToken: CONFIG.API.ACCESS_TOKEN,
            },
        });

        if (response.status === 200) {
            return response.data.cloudAccount;
        } else {
            throw new Error(`Unexpected status code: ${response.status}`);
        }
    } catch (error) {
        if (error.response && error.response.status === 404) {
            console.warn(`Account not found for GUID: ${accountGuid}`);
            return null; // Handle 404 case by returning null
        } else {
            console.error('Error fetching account by GUID:', error);
            throw error;
        }
    }
};

/**
 * Update an account by accountGuid.
 * @param {string} accountGuid - The unique identifier for the account.
 * @param {Object} updateData - The data to update (modifiedFields and version).
 * @returns {Promise<Object>} - The response from the update operation.
 */
export const updateAccount = async (accountGuid, updateData) => {
    try {
        const url = `${CONFIG.API.BASE_URL}/cloud-accounts/update/${accountGuid}`;
        const response = await axios.patch(url, updateData, {
            headers: {
                ...CONFIG.DEFAULT_HEADERS,
                accessToken: CONFIG.API.ACCESS_TOKEN,
            },
        });

        if (response.status === 200) {
            return response.data;
        } else {
            throw new Error(`Error: ${response.status} - ${response.data.message}`);
        }
    } catch (error) {
        console.error('Error updating account:', error);
        throw error;
    }
};

/**
 * Create a new account.
 * @param {Object} accountData - The account data (e.g., name, email).
 * @returns {Promise<Object>} - The created account data.
 */
export const createAccount = async (accountData) => {
    const accountGuid = uuidv4();
    const payload = {
        ...accountData,
        accountGuid,
    };

    try {
        const url = `${CONFIG.API.BASE_URL}/cloud-accounts/create`;
        const response = await axios.post(url, payload, {
            headers: {
                ...CONFIG.DEFAULT_HEADERS,
                'Content-Type': 'application/json',
                accessToken: CONFIG.API.ACCESS_TOKEN,
            },
        });

        if (response.status === 201) {
            return response.data;
        } else {
            throw new Error(`Error: ${response.status} - ${response.data.message}`);
        }
    } catch (error) {
        console.error('Error creating account:', error);
        throw error;
    }
};